import type { RequirementReviewInput } from '@/components/agent-chat/question-card-input-context';
import type { CustomGraph } from '@/types/custom-playbooks';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface AiChatContext {
  type: 'general' | 'lead_list' | 'agent' | 'sequence';
  lead_list_id?: string;
  list_name?: string;
  lead_count?: number;
  /** The agent open in its workspace, for `type: 'agent'`. */
  agent_id?: string;
  agent_name?: string;
  /** The sequence open in the builder, for `type: 'sequence'`. */
  sequence_id?: string;
  sequence_name?: string;
}

/**
 * The agent workspace's layout (layout v2), published for the dock beside it,
 * which sizes the agent's chat from it. `guided`: no panel, the guided screen
 * fills the chat; `normal`: the tabs in the panel at the user's width; `play`:
 * the Play tab wide, the chat at its narrowest; `hidden`: the user put the
 * panel away.
 */
export type AgentPanelLayout = 'guided' | 'normal' | 'play' | 'hidden';

/** What the agent's chat shows in place of its messages. */
export type AgentChatLanding = 'guided' | 'home';

export interface AgentWorkspaceLayout {
  layout: AgentPanelLayout;
  landing: AgentChatLanding | null;
}

export interface AgentFilterCondition {
  id: string;
  field: string;
  operator: string;
  value: string;
  logic?: 'and' | 'or';
}

/**
 * A play as Clu writes it (the stream's `play_draft`): its agent's canvas
 * shows it read only, for its play and chat, until the proposal it becomes
 * is loaded there.
 */
export interface AgentPlayDraft {
  /** `${conversationId}:${turnId}`: one draft per chat and turn. */
  key: string;
  agentId: string;
  /** The play it changes, or "new". */
  playTarget: string;
  conversationId: string;
  /** The writer's attempt: an older attempt never replaces a newer one. */
  attempt: number;
  /** "Writing your play: step 3 of 5". */
  label: string;
  /** The steps written so far; null before the first. */
  graph: CustomGraph | null;
  added: string[];
  changed: string[];
  /** The proposal the turn stored from it, and when it said so. */
  handedOver?: string;
  handedOverAt?: number;
}

/** Redux-safe version of AgentMessage (timestamp stored as ISO string) */
export interface SerializedAgentMessage {
  userInput?: RequirementReviewInput;
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Stable idempotency identity for this user-authored logical turn. */
  clientTurnId?: string;
  thinking?: string;
  /** clu_model_v1 router: the model that answered. */
  model?: string;
  toolResults?: {
    id: string;
    name: string;
    result: Record<string, unknown>;
    display: { type?: string; [key: string]: unknown };
    input_args?: Record<string, unknown>;
    textOffset?: number;
    caption?: string;
    durationMs?: number;
  }[];
  /** The stretches Clu spent thinking in a live reply (ThinkingSpell). */
  thoughts?: {
    startedAt: number;
    durationMs?: number;
    textOffset: number;
    afterResults: number;
  }[];
  confirmationRequest?: {
    id: string;
    /** Stable identity for retries of this confirmed-action turn. */
    clientTurnId?: string;
    /** Stable identity for retries of this cancellation turn. */
    cancelClientTurnId?: string;
    type?: string;
    tool_name: string;
    arguments: Record<string, unknown>;
    description: string;
    credits_required?: number;
    credits_available?: number;
    expires_at?: string;
    action_token?: string;
    lists?: {
      id: string;
      name: string;
      lead_count: number;
      created_at: string | null;
    }[];
  };
  timestamp: string; // ISO string
  /** When a live reply started and finished streaming (ms since epoch). */
  startedAt?: number;
  completedAt?: number;
}

export interface SerializedChatState {
  messages: SerializedAgentMessage[];
  conversationId: string | null;
  awaitingConfirmation: boolean;
  pendingConfirmation: SerializedAgentMessage['confirmationRequest'] | null;
  /** The chat's context meter (see components/agent-chat/context-meter.util). */
  contextMeter?: {
    total: number;
    capacity: number;
    basis: 'compaction' | 'model_window';
    rows: {
      system: number;
      instructions: number;
      tools: number;
      guides: number;
      conversation: number;
    };
  } | null;
  /** clu_model_v1 router: the chat's model pick ('auto' or a model key). */
  modelPick?: string;
  /**
   * The user chose `modelPick` (a new chat without it follows the member's
   * default when it is put back).
   */
  modelPickChosen?: boolean;
  lastUserMessage?: string;
}

/** Payload for the global insufficient-credits dialog (CreditGateDialog,
 *  mounted once in AppShell). Call sites that hit a 402 dispatch
 *  openCreditGate instead of a dead-end toast. */
export interface CreditGateState {
  open: boolean;
  /** Credits the blocked action needed — omitted when the caller can't
   *  compute it (the dialog then leads with the balance alone). */
  needed?: number;
  /** Short action slug for copy + analytics, e.g. 'enrich-email'. */
  action?: string;
}

interface UIState {
  /** Owner of transient chat state. Other UI preferences are not reset with it. */
  aiChatScope?: string;
  aiChatOpen: boolean;
  aiChatInitialMessage?: string;
  /**
   * clu_model_v1 router: the model the home launcher's picker chose for the
   * chat `aiChatInitialMessage` starts (a pick key, never Auto: none means
   * Auto). One-shot with the message: it goes when the message does, and a
   * chat the runtime clears first (which starts every chat on Auto) does not
   * take it with it.
   */
  aiChatInitialModelPick?: string;
  /**
   * A message another surface sends as the user into the CURRENT Clu
   * conversation (the agent workspace's own composer, a starter card). Unlike
   * `aiChatInitialMessage` it never starts a fresh thread. One-shot: the
   * runtime provider sends it once no stream is running, then clears it.
   */
  aiChatPendingSend?: string;
  /**
   * A saved Clu chat another surface opens in the dock (the Find search bar's
   * question, already answered there). One-shot: the runtime provider opens
   * it, then clears it.
   */
  aiChatOpenConversationId?: string;
  /**
   * Text another surface puts in the dock's composer for the user to finish
   * (the agent workspace's "Create with Clu"), with focus. `seq` makes the
   * same text twice a new request. One-shot: the Thread applies and clears it.
   */
  aiChatComposerPrefill?: { text: string; seq: number };
  aiChatWelcomeMessage?: string;
  aiChatContext?: AiChatContext;
  /** Set by the agent workspace while it is open (layout v2). */
  agentWorkspace?: AgentWorkspaceLayout;
  aiChatConversationId?: string;
  aiChatState?: SerializedChatState;
  /** Open Copilot sessions + the focused one.
   *  In-memory like aiChatState, so sessions survive an AppShell remount. */
  aiChatSessions?: { keys: string[]; activeKey: string };
  /** Chat state per extra session. The primary session keeps aiChatState /
   *  aiChatConversationId. */
  aiChatSessionStates?: Record<string, SerializedChatState>;
  aiChatInputValue?: string;
  agentApplyFilters?: {
    listId: string;
    filters: AgentFilterCondition[];
  };
  /** The plays Clu is writing, by chat and turn. */
  agentPlayDrafts?: Record<string, AgentPlayDraft>;
  creditGate?: CreditGateState;
}

const initialState: UIState = {
  aiChatOpen: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setAiChatScope: (state, action: PayloadAction<string>) => {
      if (state.aiChatScope && state.aiChatScope !== action.payload) {
        state.aiChatInitialMessage = undefined;
        state.aiChatInitialModelPick = undefined;
        state.aiChatPendingSend = undefined;
        state.aiChatOpenConversationId = undefined;
        state.aiChatComposerPrefill = undefined;
        state.aiChatWelcomeMessage = undefined;
        state.aiChatContext = undefined;
        state.agentWorkspace = undefined;
        state.aiChatConversationId = undefined;
        state.aiChatState = undefined;
        state.aiChatSessions = undefined;
        state.aiChatSessionStates = undefined;
        state.aiChatInputValue = undefined;
        state.agentApplyFilters = undefined;
        state.agentPlayDrafts = undefined;
      }
      state.aiChatScope = action.payload;
    },
    toggleAiChat: (state) => {
      state.aiChatOpen = !state.aiChatOpen;
      if (!state.aiChatOpen) {
        state.aiChatInitialMessage = undefined;
        state.aiChatInitialModelPick = undefined;
        state.aiChatWelcomeMessage = undefined;
      }
    },
    setAiChatOpen: (state, action: PayloadAction<boolean>) => {
      state.aiChatOpen = action.payload;
      if (!action.payload) {
        state.aiChatInitialMessage = undefined;
        state.aiChatInitialModelPick = undefined;
        state.aiChatWelcomeMessage = undefined;
      }
    },
    setAiChatInitialMessage: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.aiChatInitialMessage = action.payload;
      // The model belongs to its message: a message another surface seeds
      // starts without one, and clearing a message clears it. The home
      // launcher sets its model after its message.
      state.aiChatInitialModelPick = undefined;
    },
    setAiChatInitialModelPick: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.aiChatInitialModelPick = action.payload;
    },
    sendAiChatMessage: (state, action: PayloadAction<string | undefined>) => {
      state.aiChatPendingSend = action.payload;
    },
    openAiChatConversation: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.aiChatOpenConversationId = action.payload;
    },
    prefillAiChatComposer: (state, action: PayloadAction<string>) => {
      state.aiChatComposerPrefill = {
        text: action.payload,
        seq: (state.aiChatComposerPrefill?.seq ?? 0) + 1,
      };
    },
    clearAiChatComposerPrefill: (state) => {
      state.aiChatComposerPrefill = undefined;
    },
    setAiChatContext: (
      state,
      action: PayloadAction<AiChatContext | undefined>
    ) => {
      state.aiChatContext = action.payload;
    },
    setAgentWorkspaceLayout: (
      state,
      action: PayloadAction<AgentWorkspaceLayout | undefined>
    ) => {
      state.agentWorkspace = action.payload;
    },
    setAiChatConversationId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.aiChatConversationId = action.payload;
    },
    setAiChatState: (
      state,
      action: PayloadAction<SerializedChatState | undefined>
    ) => {
      state.aiChatState = action.payload;
    },
    setAiChatSessions: (
      state,
      action: PayloadAction<{ keys: string[]; activeKey: string } | undefined>
    ) => {
      state.aiChatSessions = action.payload;
    },
    /** Omitting chatState drops the session's persisted state. */
    setAiChatSessionState: (
      state,
      action: PayloadAction<{ key: string; chatState?: SerializedChatState }>
    ) => {
      const { key, chatState } = action.payload;
      state.aiChatSessionStates ??= {};
      if (chatState) state.aiChatSessionStates[key] = chatState;
      else delete state.aiChatSessionStates[key];
    },
    setAiChatWelcomeMessage: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.aiChatWelcomeMessage = action.payload;
    },
    setAiChatInputValue: (state, action: PayloadAction<string | undefined>) => {
      state.aiChatInputValue = action.payload;
    },
    setAgentApplyFilters: (
      state,
      action: PayloadAction<
        { listId: string; filters: AgentFilterCondition[] } | undefined
      >
    ) => {
      state.agentApplyFilters = action.payload;
    },
    setAgentPlayDraft: (state, action: PayloadAction<AgentPlayDraft>) => {
      const draft = action.payload;
      const drafts = (state.agentPlayDrafts ??= {});
      const stored = drafts[draft.key];
      // Late for one handed over to its proposal: only a newer attempt.
      if (
        stored &&
        (draft.attempt < stored.attempt ||
          (stored.handedOver && draft.attempt === stored.attempt))
      )
        return;
      // A chat runs one turn at a time: its new turn's draft replaces the
      // last one's.
      for (const [key, other] of Object.entries(drafts))
        if (key !== draft.key && other.conversationId === draft.conversationId)
          delete drafts[key];
      // A label with no steps yet keeps the steps its attempt already drew;
      // a newer attempt starts over, so the refused one's steps go.
      drafts[draft.key] =
        draft.graph || !stored?.graph || draft.attempt > stored.attempt
          ? draft
          : {
              ...draft,
              graph: stored.graph,
              added: stored.added,
              changed: stored.changed,
            };
    },
    markDraftHandedOver: (
      state,
      action: PayloadAction<{ key: string; proposalId: string; at: number }>
    ) => {
      const draft = state.agentPlayDrafts?.[action.payload.key];
      if (!draft) return;
      draft.handedOver = action.payload.proposalId;
      draft.handedOverAt = action.payload.at;
    },
    clearAgentPlayDraft: (state, action: PayloadAction<string>) => {
      if (state.agentPlayDrafts) delete state.agentPlayDrafts[action.payload];
    },
    openCreditGate: (
      state,
      action: PayloadAction<Omit<CreditGateState, 'open'> | undefined>
    ) => {
      state.creditGate = { open: true, ...(action.payload ?? {}) };
    },
    closeCreditGate: (state) => {
      state.creditGate = undefined;
    },
  },
});

export const {
  setAiChatScope,
  toggleAiChat,
  setAiChatOpen,
  setAiChatInitialMessage,
  setAiChatInitialModelPick,
  sendAiChatMessage,
  openAiChatConversation,
  prefillAiChatComposer,
  clearAiChatComposerPrefill,
  setAiChatWelcomeMessage,
  setAiChatContext,
  setAgentWorkspaceLayout,
  setAiChatConversationId,
  setAiChatState,
  setAiChatSessions,
  setAiChatSessionState,
  setAiChatInputValue,
  setAgentApplyFilters,
  setAgentPlayDraft,
  markDraftHandedOver,
  clearAgentPlayDraft,
  openCreditGate,
  closeCreditGate,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;

export const selectAiChatOpen = (state: { ui: UIState }) => state.ui.aiChatOpen;
export const selectAiChatInitialMessage = (state: { ui: UIState }) =>
  state.ui.aiChatInitialMessage;
export const selectAiChatInitialModelPick = (state: { ui: UIState }) =>
  state.ui.aiChatInitialModelPick;
export const selectAiChatPendingSend = (state: { ui: UIState }) =>
  state.ui.aiChatPendingSend;
export const selectAiChatOpenConversationId = (state: { ui: UIState }) =>
  state.ui.aiChatOpenConversationId;
export const selectAiChatComposerPrefill = (state: { ui: UIState }) =>
  state.ui.aiChatComposerPrefill;
export const selectAiChatContext = (state: { ui: UIState }) =>
  state.ui.aiChatContext;
export const selectAgentWorkspaceLayout = (state: { ui: UIState }) =>
  state.ui.agentWorkspace;
export const selectAiChatConversationId = (state: { ui: UIState }) =>
  state.ui.aiChatConversationId;
export const selectAiChatState = (state: { ui: UIState }) =>
  state.ui.aiChatState;
export const selectAiChatSessions = (state: { ui: UIState }) =>
  state.ui.aiChatSessions;
export const selectAiChatInputValue = (state: { ui: UIState }) =>
  state.ui.aiChatInputValue;
export const selectAgentApplyFilters = (state: { ui: UIState }) =>
  state.ui.agentApplyFilters;
export const selectAgentPlayDrafts = (state: { ui: UIState }) =>
  state.ui.agentPlayDrafts;
export const selectCreditGate = (state: { ui: UIState }) => state.ui.creditGate;
